const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export interface SeoCouponInput {
  type?: string | null;
  discount_type?: string | null;
  discount_value?: number | null;
  is_active?: boolean | null;
  expires_at?: string | null;
}

/**
 * Maior desconto anunciável: SOMENTE cupons do tipo `code`, ativos e dentro da
 * validade. Ofertas, cupons expirados/inativos e comissão nunca entram.
 * Percentual tem prioridade sobre valor fixo. Retorna null quando não há
 * código confiável.
 */
export function getAnnounceableDiscount(coupons: SeoCouponInput[]): string | null {
  const now = Date.now();
  const valid = (coupons ?? []).filter(
    (c) =>
      c.type === "code" &&
      c.is_active !== false &&
      (!c.expires_at || new Date(c.expires_at).getTime() > now) &&
      !!c.discount_value &&
      c.discount_value! > 0,
  );
  const percent = valid.filter((c) => c.discount_type === "percentage");
  if (percent.length > 0) {
    return `${Math.round(Math.max(...percent.map((c) => c.discount_value!)))}% OFF`;
  }
  const fixed = valid.filter((c) => c.discount_type === "fixed");
  if (fixed.length > 0) {
    return `R$${Math.round(Math.max(...fixed.map((c) => c.discount_value!)))} OFF`;
  }
  return null;
}

/**
 * Prefixo do H1/título. Lojas em que a busca dominante é "código promocional"
 * (Booking, Azul...) ligam o switch no admin (`brands.seo_prefix_promocional`).
 */
export function brandTitlePrefix(promocional?: boolean | null): string {
  return promocional ? "Código Promocional" : "Cupom";
}

export function brandDescriptionPrefix(promocional?: boolean | null): string {
  return promocional ? "Códigos promocionais" : "Cupons";
}

/**
 * Meta title da loja. Formato: `{prefixo} {Nome} | {sufixo} - {Mês Ano}`.
 * O nome da loja NUNCA é abreviado nem cortado: quando o título passa de 60
 * caracteres, sai primeiro o mês/ano e depois o sufixo de descontos.
 */
export function buildBrandSeoTitle(
  brandName: string,
  coupons: SeoCouponInput[],
  promocional?: boolean | null,
): string {
  const discount = getAnnounceableDiscount(coupons);
  const titleSuffix = discount ? `Descontos até ${discount}` : "Ofertas e Descontos";

  const now = new Date();
  const monthYear = `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
  const name = brandName.trim();
  const prefix = brandTitlePrefix(promocional);

  const full = `${prefix} ${name} | ${titleSuffix} - ${monthYear}`;
  if (full.length <= 60) return full;

  const withoutMonth = `${prefix} ${name} | ${titleSuffix}`;
  if (withoutMonth.length <= 60) return withoutMonth;

  return `${prefix} ${name}`;
}

/**
 * Meta description da loja, coerente com o mesmo desconto usado no título.
 */
export function buildBrandSeoDescription(
  brandName: string,
  categoryName: string | null | undefined,
  coupons: SeoCouponInput[],
  promocional?: boolean | null,
): string {
  const discount = getAnnounceableDiscount(coupons);
  const context = categoryName?.trim() || brandName;
  // Evita vício de linguagem: no modo promocional a frase final usa "cupons".
  const validTxt = promocional ? "cupons válidos" : "códigos válidos";
  return `${brandDescriptionPrefix(promocional)} ${brandName}${discount ? ` ${discount}` : ""} em ${context}. Confira ${validTxt} e ofertas atualizadas para economizar hoje.`.slice(0, 160);
}

/**
 * O recálculo de `seo_title`/`seo_description` passou a ser feito no banco por
 * trigger em `coupons` (`recompute_brand_seo`), respeitando `brands.seo_locked`.
 * Mantido apenas para compatibilidade com chamadas existentes.
 */
export async function recomputeBrandSeoTitle(_brandId: string): Promise<void> {
  return;
}
